import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { WIZARD_STEPS } from '@rat/shared';
import { ratRoleLabel } from '../auth/access';
import { fetchObservationThread, type Observation, type ThreadEntry } from '../lib/queries';

const STEP_LABEL: Record<string, string> = Object.fromEntries(
  WIZARD_STEPS.map((s) => [s.key, s.label]),
);
STEP_LABEL['general'] = 'General';

export function fieldLabel(path: string): string {
  const [step] = path.split('.');
  return STEP_LABEL[step ?? 'general'] ?? path;
}

const KIND_LABEL: Record<ThreadEntry['kind'], string> = {
  comentario: 'Comentario',
  respuesta: 'Respuesta de la unidad',
  resolucion: 'Resuelta por el revisor',
  reapertura: 'Reabierta por el revisor',
};

/** D1 guarda 'YYYY-MM-DD HH:MM:SS' en UTC. */
function when(createdAt: string): string {
  const d = new Date(createdAt.includes('T') ? createdAt : `${createdAt.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? createdAt : d.toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Una observacion del revisor.
 *  - `onToggle`: solo el REVISOR (confirma o reabre).
 *  - `onRespond`: solo la UNIDAD, y solo mientras la observacion esta abierta o reabierta. La unidad NO resuelve:
 *    responde, y el revisor confirma. Sin responder las obligatorias no se puede marcar la actividad como corregida.
 */
export function ObservationItem({
  o,
  onToggle,
  busy,
  onRespond,
  respondPending,
}: {
  o: Observation;
  onToggle?: (resolved: boolean) => void;
  busy?: boolean;
  onRespond?: (body: string) => void;
  respondPending?: boolean;
}) {
  const resolved = !!o.resolved_at;
  const [responding, setResponding] = useState(false);
  const [reply, setReply] = useState('');
  const [showThread, setShowThread] = useState(false);
  const thread = useQuery({
    queryKey: ['observation', o.id, 'thread', o.state],
    queryFn: () => fetchObservationThread(o.id),
    enabled: showThread,
  });
  const needsReply = o.state === 'abierta' || o.state === 'reabierta';

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${o.severity === 'obligatoria' ? 'var(--critical)' : 'var(--warning)'}`,
        borderRadius: 'var(--r-sm)',
        padding: '9px 11px',
        opacity: resolved ? 0.6 : 1,
        background: 'var(--surface)',
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span
          className="pill"
          style={{
            background: o.severity === 'obligatoria' ? 'var(--critical-weak)' : 'var(--warning-weak)',
            color: o.severity === 'obligatoria' ? 'var(--critical)' : 'var(--warning)',
          }}
        >
          {o.severity === 'obligatoria' ? 'Obligatoria' : 'Sugerida'}
        </span>
        <span className="muted" style={{ fontSize: 12 }}>{fieldLabel(o.field_path)}</span>
        {resolved && <span className="pill pill--success">Resuelta</span>}
        {o.state === 'respondida' && !resolved && <span className="pill pill--accent">Respondida · falta confirmación</span>}
        {o.state === 'reabierta' && <span className="pill">Reabierta</span>}
        <span style={{ flex: 1 }} />
        {o.state !== 'abierta' && (
          <button className="btn btn--ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => setShowThread((v) => !v)}>
            {showThread ? 'Ocultar hilo' : 'Ver hilo'}
          </button>
        )}
        {onToggle && (
          <button
            className="btn btn--ghost"
            style={{ fontSize: 12, padding: '3px 8px' }}
            disabled={busy}
            onClick={() => onToggle(!resolved)}
          >
            {resolved ? 'Reabrir' : 'Marcar resuelta'}
          </button>
        )}
        {onRespond && needsReply && !responding && (
          <button className="btn btn--ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => setResponding(true)}>
            Responder
          </button>
        )}
      </div>
      <p style={{ margin: '6px 0 0', fontSize: 13 }}>{o.text}</p>

      {showThread && (
        <div style={{ marginTop: 8, display: 'grid', gap: 6, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
          {thread.isLoading && <span className="muted" style={{ fontSize: 12 }}>Cargando…</span>}
          {thread.error && <span className="muted" style={{ fontSize: 12 }}>No se pudo cargar el hilo.</span>}
          {(thread.data ?? []).length === 0 && !thread.isLoading && !thread.error && (
            <span className="muted" style={{ fontSize: 12 }}>Sin respuestas todavía.</span>
          )}
          {(thread.data ?? []).map((e) => (
            <div key={e.id} style={{ fontSize: 12 }}>
              <span className="muted">
                {KIND_LABEL[e.kind] ?? e.kind} · {ratRoleLabel(e.author_role)} · {when(e.created_at)}
              </span>
              <div style={{ marginTop: 2, fontSize: 13 }}>{e.body}</div>
            </div>
          ))}
        </div>
      )}

      {onRespond && needsReply && responding && (
        <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
          <textarea
            rows={3}
            placeholder="Explique qué corrigió o por qué no corresponde"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            style={{ resize: 'vertical', padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--ground)', color: 'var(--ink)', font: 'inherit' }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn--primary"
              disabled={reply.trim().length < 3 || respondPending}
              onClick={() => onRespond(reply.trim())}
            >
              {respondPending ? 'Enviando…' : 'Enviar respuesta'}
            </button>
            <button className="btn btn--ghost" disabled={respondPending} onClick={() => { setResponding(false); setReply(''); }}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
